import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Container } from "react-bootstrap";
import DynamicBreadcrumb from "../header/breadcrumb";
import { Link } from "react-router-dom";
import offerAPI from "../../services/offerapi";
import moment from "moment";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import { showConfirm } from "react-confirm-prompt";
import BranchesModal from "./BranchesModal";
import branchApi from "../../services/branchapi";

const BogoList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOffer, setTotalOffer] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "desc" }]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [refreshTable, setRefreshTable] = useState(false);

  const [branches, setBranches] = useState([]);
  const [branchesModalVisible, setBranchesModalVisible] = useState(false);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState(null);

  const pathArray = useMemo(() => [{ name: "BOGO Offers List", url: null }], []);

  const offerTypes = useMemo(
    () => ({
      4: "BOGO - Same Product Free",
      5: "BOGO - Another Product Free",
    }),
    []
  );

  const statusArr = useMemo(
    () => ({
      1: "Active",
      2: "Inactive",
    }),
    []
  );

  const statusColors = useMemo(
    () => ({
      1: "success",
      2: "danger",
    }),
    []
  );

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const data = await offerAPI.getAllOffers({
        limit: pageSize,
        page,
        sortModel,
        filterModel: {
          ...filterModel,
          offerTypes: [4, 5],
        },
      });

      const formattedRows = (data?.data?.result || []).map((cust, index) => ({
        id: cust.id,
        sno: index + 1 + page * pageSize,
        created_at: cust.created_at
          ? moment(cust.created_at).format("YYYY/MM/DD HH:mm:ss")
          : "-",
        offer_start_date: cust.offer_start_date
          ? moment(cust.offer_start_date).format("YYYY/MM/DD HH:mm:ss")
          : "-",
        offer_end_date: cust.offer_end_date
          ? moment(cust.offer_end_date).format("YYYY/MM/DD HH:mm:ss")
          : "-",
        offer_name: cust.offer_name,
        offer_type: cust.offer_type,
        offerType: offerTypes[cust.offer_type] || "Others",
        status: cust.status,
        offerBranches: cust.offerBranches || [],
        productCount: cust.productCount || 0,
      }));

      setRows(formattedRows);
      setTotalOffer(data?.data?.totalRecords?.[0]?.total_records || 0);
    } catch (error) {
      console.error("Failed to fetch BOGO offers:", error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, filterModel, refreshTable, offerTypes]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await branchApi.getAllBranches({
          limit: 200,
          page: 0,
          status: 1,
          sortModel: [{ field: "branchName", sort: "asc" }],
          filterModel: [],
        });
        setBranches(response.data.result || []);
      } catch (error) {
        console.error("Failed to fetch branches:", error);
      }
    };

    fetchBranches();
  }, []);

  const submitUpdateDetails = async (offerId, currentStatus) => {
    try {
      const sendStatus = currentStatus === 1 ? 2 : 1;

      const params = {
        action: "status",
        status: sendStatus,
        offerId,
        reason: null,
      };

      const resp = await offerAPI.updateOfferStatus(params);

      if (resp) {
        await showConfirm("Success", {
          description: resp.message || "Offer status updated successfully",
          type: "success",
          hideCancel: true,
        });
        setRefreshTable((prev) => !prev);
      } else {
        await showConfirm("Oops!", {
          description: "Process failed, kindly try again later!",
          type: "warning",
          hideCancel: true,
        });
      }
    } catch (error) {
      await showConfirm("Oops!", {
        description: error?.response?.data?.message || "Process failed",
        type: "warning",
        hideCancel: true,
      });
    }
  };

  const handleStatusClick = (offerId, currentStatus) => {
    const title =
      currentStatus === 1
        ? "Are you sure you want to set this BOGO offer to inactive?"
        : "Are you sure you want to set this BOGO offer to active?";

    showConfirm(title, {
      description: "This will update the offer status",
      type: "info",
      confirmText: "Confirm",
      cancelText: "Cancel",
    }).then((answer) => {
      if (answer) submitUpdateDetails(offerId, currentStatus);
    });
  };

  const handleDeleteOffer = (offerId) => {
    showConfirm("Are you sure you want to delete this BOGO offer?", {
      description: "This action cannot be reverted.",
      type: "warning",
      confirmText: "Delete",
      cancelText: "Cancel",
    }).then(async (answer) => {
      if (!answer) return;

      try {
        const resp = await offerAPI.updateOfferStatus({
          action: "status",
          status: 9,
          offerId,
          reason: null,
        });

        await showConfirm("Success", {
          description: resp?.message || "Offer deleted successfully",
          type: "success",
          hideCancel: true,
        });

        setRefreshTable((prev) => !prev);
      } catch (error) {
        await showConfirm("Oops!", {
          description: error?.response?.data?.message || "Delete failed",
          type: "warning",
          hideCancel: true,
        });
      }
    });
  };

  const handleOpenBranchesModal = (row) => {
    setAvailableBranches(row.offerBranches || []);
    setSelectedOfferId(row.id);
    setBranchesModalVisible(true);
  };

  const handleCloseBranchesModal = () => {
    setBranchesModalVisible(false);
  };

  const columns = useMemo(
    () => [
      { field: "sno", headerName: "S NO", width: 90, sortable: false },
      { field: "created_at", headerName: "Created On", flex: 1 },
      { field: "offer_name", headerName: "Offer Name", flex: 1.3 },
      { field: "offerType", headerName: "Offer Type", flex: 1.1 },
      { field: "offer_start_date", headerName: "Start On", flex: 1 },
      { field: "offer_end_date", headerName: "End On", flex: 1 },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        renderCell: (params) => {
          const status = params.value;
          const statusColor = statusColors[status] || "secondary";
          const statusName = statusArr[status] || "Pending";

          return (
            <Badge
              pill
              bg={statusColor}
              style={{ cursor: "pointer" }}
              onDoubleClick={() => handleStatusClick(params.row.id, status)}
              title="Double click to toggle status"
            >
              {statusName}
            </Badge>
          );
        },
      },
      {
        field: "branches",
        headerName: "Branches",
        flex: 0.9,
        sortable: false,
        renderCell: (params) => (
          <Button
            variant="warning"
            className="text-white"
            size="sm"
            onClick={() => handleOpenBranchesModal(params.row)}
          >
            View Branches
          </Button>
        ),
      },
      {
        field: "action",
        headerName: "Action",
        flex: 1.2,
        sortable: false,
        renderCell: (params) => (
          <div className="d-flex gap-2">
            <Link
              to={`/admin/offers/manual/edit/${params.row.id}`}
              className="btn btn-sm btn-primary"
            >
              Edit
            </Link>

            {Number(params.row.productCount || 0) > 0 ? (
              <Link
                to={`/offers/details/${params.row.id}`}
                className="btn btn-sm btn-info text-white"
              >
                View
              </Link>
            ) : (
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleDeleteOffer(params.row.id)}
              >
                Delete
              </button>
            )}
          </div>
        ),
      },
    ],
    [statusArr, statusColors]
  );

  return (
    <Container fluid className="py-3">
      <DynamicBreadcrumb pathArray={pathArray} />

      <div className="d-flex justify-content-end mb-3">
        <Link className="btn btn-success" to="/admin/offers/manual/create">
          Create BOGO Offer
        </Link>
      </div>

      <Box
        sx={{
          height: 650,
          width: "100%",
          [`& .${gridClasses.row}`]: {
            bgcolor: "background.paper",
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={totalOffer}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          pageSizeOptions={[15, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
        />
      </Box>

      <BranchesModal
        branches={branches}
        availableBranches={availableBranches}
        branchesModalVisible={branchesModalVisible}
        handleCloseBranchesModal={handleCloseBranchesModal}
        selectedOfferId={selectedOfferId}
      />
    </Container>
  );
};

export default BogoList;